import * as THREE from "three";

export class GameLogic {
  constructor(scene, grid, uiManager) {
    this.scene = scene;
    this.grid = grid;
    this.uiManager = uiManager;
    this.score = 0;
    this.totalScore = 0;
    this.currentLevel = 1;
    this.currentPhase = 1;
    this.energy = 0;
    this.maxEnergy = 100; // Default max energy
    this.negativeCrystalsRemoved = 0;
    this.connectionsMade = 0;
    this.moves = 0;
    this.maxMoves = 30;
    this.selectedCrystal = null;
    this.validMoves = [];
    this.isProcessingMove = false;

    // Initialize default goals
    this.goals = {
      negativeCrystalsToRemove: 3,
      energyToCollect: 30,
      connectionsRequired: 1,
    };

    // Chakra progression order from root to crown
    this.chakraOrder = [
      "red", // Muladhara (Root)
      "orange", // Svadhishthana (Sacral)
      "yellow", // Manipura (Solar Plexus)
      "green", // Anahata (Heart)
      "blue", // Vishuddha (Throat)
      "indigo", // Ajna (Third Eye)
      "purple", // Sahasrara (Crown)
    ];
  }

  setGrid(grid) {
    this.grid = grid;
  }

  setUIManager(uiManager) {
    this.uiManager = uiManager;
    this.updateUI();
  }

  setInputHandler(inputHandler) {
    this.inputHandler = inputHandler;
  }

  updateUI() {
    if (this.uiManager) {
      this.uiManager.updateGameState(this.getGameState());
    }
  }

  validateConnection(startCrystal, endCrystal, currentPath) {
    // If connecting to an empty space, always allow it if we have a path started
    if (!endCrystal) {
      return currentPath && currentPath.length > 0;
    }

    // If this is the first crystal in the path, it must be positive
    if (currentPath.length === 0 && startCrystal.type !== "positive") {
      return false;
    }

    // Get the first colored crystal in the path
    const firstColoredCrystal = currentPath[0];

    // If this is a neutral or negative crystal, allow the connection
    if (endCrystal.type === "neutral" || endCrystal.type === "negative") {
      return true;
    }

    // If connecting to a colored crystal
    if (endCrystal.type === "color" || endCrystal.type === "positive") {
      // Always use the first colored crystal as the start point for validation
      return this.validateChakraConnection(firstColoredCrystal, endCrystal);
    }

    return false;
  }

  checkClearPath(startPos, endPos) {
    // Only check for direct paths (horizontal, vertical, or diagonal)
    const rowDiff = endPos.row - startPos.row;
    const colDiff = endPos.col - startPos.col;

    // Must be in a straight line
    if (
      Math.abs(rowDiff) !== Math.abs(colDiff) &&
      rowDiff !== 0 &&
      colDiff !== 0
    ) {
      return false;
    }

    // Get step direction
    const rowStep = rowDiff === 0 ? 0 : rowDiff / Math.abs(rowDiff);
    const colStep = colDiff === 0 ? 0 : colDiff / Math.abs(colDiff);

    // Check each position along the path
    let currentRow = startPos.row + rowStep;
    let currentCol = startPos.col + colStep;
    const steps = Math.max(Math.abs(rowDiff), Math.abs(colDiff));

    for (let i = 1; i < steps; i++) {
      // If any crystal exists in the path, it's blocked
      if (this.grid.crystals[currentRow][currentCol] !== null) {
        return false;
      }
      currentRow += rowStep;
      currentCol += colStep;
    }

    return true;
  }

  validateIntermediateCrystal(currentPath, nextCrystal) {
    if (!currentPath || currentPath.length === 0) return false;

    // Allow connections to neutral/negative crystals
    if (nextCrystal.type === "neutral" || nextCrystal.type === "negative") {
      return true;
    }

    // For colored crystals, use the first colored crystal in the path for validation
    const firstColoredCrystal = currentPath.find(
      (crystal) =>
        crystal && (crystal.type === "positive" || crystal.type === "color")
    );

    if (!firstColoredCrystal) return false;

    return this.validateChakraConnection(firstColoredCrystal, nextCrystal);
  }

  validateEmptyPosition(currentPath, row, col) {
    // Empty positions are valid if:
    // 1. We have a path started
    // 2. The first crystal is a positive/colored crystal
    if (!currentPath || currentPath.length === 0) {
      console.log("Empty position invalid: No path started");
      return false;
    }

    const firstCrystal = currentPath[0];
    const isValidStart =
      firstCrystal.type === "positive" || firstCrystal.type === "color";
    console.log("Empty position validation:", {
      firstCrystalType: firstCrystal.type,
      isValidStart,
    });
    return isValidStart;
  }

  animateNeutralTransformation(crystal, targetColor) {
    // Check if crystal and its meshes exist
    if (
      !crystal ||
      !crystal.mesh ||
      !crystal.crystalMesh ||
      !crystal.crystalMesh.material
    ) {
      console.warn("Invalid crystal for animation");
      return;
    }

    const duration = 1500; // 1.5 seconds
    const startTime = Date.now();
    const startColor = new THREE.Color(0xffffff);
    const endColor = new THREE.Color(targetColor);
    const startPosition = crystal.mesh.position.y;
    const startScale = crystal.mesh.scale.clone();
    const startRotation = crystal.mesh.rotation.y;
    const maxHeight = 1.0; // Original elevation height
    const maxScale = 1.5; // Original peak scale
    const spinSpeed = Math.PI * 2.5; // Original spin speed (1.25 rotations)

    const animate = () => {
      // Check if crystal is still valid
      if (
        !crystal ||
        !crystal.mesh ||
        !crystal.crystalMesh ||
        !crystal.crystalMesh.material
      ) {
        console.warn("Crystal became invalid during animation");
        return;
      }

      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Smooth easing function
      const easeInOut = (t) =>
        t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      const easeProgress = easeInOut(progress);

      // Color transition
      const currentColor = new THREE.Color().lerpColors(
        startColor,
        endColor,
        easeProgress
      );
      crystal.crystalMesh.material.color.copy(currentColor);
      crystal.crystalMesh.material.emissive.copy(currentColor);
      crystal.crystalMesh.material.emissiveIntensity = 0.2 + easeProgress * 0.6; // Original glow intensity

      // Elevation animation (up then down)
      const elevationProgress = Math.sin(progress * Math.PI);
      crystal.mesh.position.y = startPosition + elevationProgress * maxHeight;

      // Scale animation (grow then shrink)
      const scale = 1 + (maxScale - 1) * Math.sin(progress * Math.PI);
      crystal.mesh.scale.set(scale, scale, scale);

      // Rotation animation (accelerate then decelerate)
      crystal.mesh.rotation.y = startRotation + spinSpeed * easeProgress;

      // Continue animation if not complete
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // Reset to final state
        crystal.mesh.position.y = startPosition;
        crystal.mesh.scale.copy(startScale);
        crystal.type = "positive";
        crystal.color = targetColor;
      }
    };

    // Start animation
    animate();
  }

  processConnection(startCrystal, endCrystal, path) {
    // For final connection, must end on a positive crystal
    if (endCrystal.type !== "positive" && endCrystal.type !== "color") {
      console.log("Invalid: End crystal must be a positive crystal");
      return false;
    }

    // Check if this connection matches the required chakra pattern
    const isValidConnection = this.validateChakraConnection(
      startCrystal,
      endCrystal
    );
    const isGoalConnection = this.isGoalConnection(
      startCrystal,
      endCrystal,
      path
    );

    console.log("Connection validation:", {
      isValidConnection,
      isGoalConnection,
      startChakra: this.getChakraNameFromColor(startCrystal.color),
      endChakra: this.getChakraNameFromColor(endCrystal.color),
      phase: this.currentPhase,
      activeChakras: this.grid.activeChakras,
    });

    // Process the path
    this.moves++;
    let energyGained = 0;

    // Process each crystal in the path
    path.forEach((crystal) => {
      // Find crystal position in grid
      let crystalPos = null;
      for (let row = 0; row < this.grid.gridSize; row++) {
        for (let col = 0; col < this.grid.gridSize; col++) {
          if (this.grid.crystals[row][col] === crystal) {
            crystalPos = { row, col };
            break;
          }
        }
        if (crystalPos) break;
      }

      if (crystal.type === "negative") {
        this.negativeCrystalsRemoved++;
        energyGained += 15;
        if (crystalPos) {
          this.grid.removeCrystal(crystalPos.row, crystalPos.col);
          this.grid.spawnNewCrystal(crystalPos.row, crystalPos.col);
        }
      } else if (crystal.type === "neutral") {
        // Animate the neutral crystal transformation
        this.animateNeutralTransformation(crystal, startCrystal.color);
        energyGained += 10;
      } else if (crystal.type === "positive" || crystal.type === "color") {
        energyGained += 5;
        if (crystalPos) {
          this.grid.removeCrystal(crystalPos.row, crystalPos.col);
          this.grid.spawnNewCrystal(crystalPos.row, crystalPos.col);
        }
      }
    });

    // Add bonus energy for longer paths
    energyGained += path.length * 2;

    // Update game state
    this.energy = Math.min(this.maxEnergy, this.energy + energyGained);
    this.score += energyGained + this.negativeCrystalsRemoved * 100;

    // Increment connections if it's a goal connection, regardless of validation
    // This allows goal connections to count even with neutral/negative crystals in the path
    if (isGoalConnection) {
      this.connectionsMade++;
      console.log("Goal connection counted:", {
        connectionsMade: this.connectionsMade,
        requiredConnections: this.goals.connectionsRequired,
      });
    }

    // Update UI
    this.updateUI();

    // Check if level goals are met
    return this.checkLevelCompletion();
  }

  isGoalConnection(startCrystal, endCrystal, path) {
    const startChakra = this.getChakraNameFromColor(startCrystal.color);
    const endChakra = this.getChakraNameFromColor(endCrystal.color);
    const currentPhaseChakras = this.grid.activeChakras;

    // Get only the colored crystals in the path (ignore neutral/negative)
    const coloredCrystals = path.filter(
      (crystal) =>
        crystal && (crystal.type === "positive" || crystal.type === "color")
    );
    const pathChakras = coloredCrystals
      .map((crystal) => this.getChakraNameFromColor(crystal.color))
      .filter((chakra) => chakra !== null);

    console.log("Goal connection check:", {
      startChakra,
      endChakra,
      pathChakras,
      currentPhase: this.currentPhase,
      activeChakras: currentPhaseChakras,
      fullPath: path.map((c) => c.type),
      coloredCrystalsCount: coloredCrystals.length,
    });

    switch (this.currentPhase) {
      case 1:
        // Phase 1: Single color connections of the active chakra
        return (
          startChakra === endChakra && startChakra === currentPhaseChakras[0]
        );

      case 2:
        // Phase 2: Only the specific dual connection for this level (in either direction)
        return (
          (startChakra === currentPhaseChakras[0] &&
            endChakra === currentPhaseChakras[1]) ||
          (startChakra === currentPhaseChakras[1] &&
            endChakra === currentPhaseChakras[0])
        );

      case 3:
      case 4:
      case 5:
      case 6:
      case 7:
        // For all higher phases: Must match the exact sequence or its reverse
        // Only consider the colored crystals, ignoring neutral/negative ones
        console.log("Sequence goal check:", {
          pathChakras,
          currentPhaseChakras,
          forwardSequence: pathChakras.join("-"),
          reverseSequence: [...currentPhaseChakras].reverse().join("-"),
          hasAllChakras: pathChakras.length === currentPhaseChakras.length,
          startChakra,
          endChakra,
        });

        // Must have all required chakras in sequence
        if (pathChakras.length !== currentPhaseChakras.length) {
          return false;
        }

        // Check forward sequence
        const isForwardSequence = pathChakras.every(
          (chakra, index) => chakra === currentPhaseChakras[index]
        );

        // Check reverse sequence
        const isReverseSequence = pathChakras.every(
          (chakra, index) =>
            chakra ===
            currentPhaseChakras[currentPhaseChakras.length - 1 - index]
        );

        // For forward sequence, end crystal should be the last in sequence
        // For reverse sequence, end crystal should be the first in sequence
        const hasValidEndCrystal =
          (isForwardSequence &&
            endChakra ===
              currentPhaseChakras[currentPhaseChakras.length - 1]) ||
          (isReverseSequence && endChakra === currentPhaseChakras[0]);

        return (isForwardSequence || isReverseSequence) && hasValidEndCrystal;

      default:
        return false;
    }
  }

  validateChakraConnection(startCrystal, endCrystal) {
    const startChakra = this.getChakraNameFromColor(startCrystal.color);
    const endChakra = this.getChakraNameFromColor(endCrystal.color);
    const currentPhaseChakras = this.grid.activeChakras;

    console.log("Validating connection:", {
      startChakra,
      endChakra,
      currentPhase: this.currentPhase,
      activeChakras: currentPhaseChakras,
      currentLevel: this.currentLevel,
      levelInPhase: this.getLevelInPhase(),
    });

    if (!startChakra || !endChakra) {
      console.log("Invalid: Missing chakra names");
      return false;
    }

    // Always allow same-color connections in any phase
    if (startChakra === endChakra) {
      return true;
    }

    // For phase 2, only allow the specific goal connection (in either direction)
    if (this.currentPhase === 2) {
      const isValidDual =
        (startChakra === currentPhaseChakras[0] &&
          endChakra === currentPhaseChakras[1]) ||
        (startChakra === currentPhaseChakras[1] &&
          endChakra === currentPhaseChakras[0]);

      console.log("Phase 2 connection check:", {
        startChakra,
        endChakra,
        requiredPair: currentPhaseChakras,
        isValidDual,
      });

      return isValidDual;
    }

    // For phase 3+, validate the sequence
    if (this.currentPhase >= 3) {
      // Get all colored crystals in the current path
      const currentPath = this.inputHandler.currentPath || [];
      const coloredCrystals = currentPath.filter(
        (crystal) =>
          crystal && (crystal.type === "positive" || crystal.type === "color")
      );

      // Get the colored crystals in the path so far
      const pathChakras = coloredCrystals
        .map((crystal) => this.getChakraNameFromColor(crystal.color))
        .filter((chakra) => chakra !== null);

      // If this is the start of a path, allow either end of the sequence
      if (pathChakras.length === 0) {
        const isValidStart =
          startChakra === currentPhaseChakras[0] ||
          startChakra === currentPhaseChakras[currentPhaseChakras.length - 1];
        return isValidStart;
      }

      // Check if we're building forward or reverse sequence
      const isForwardSequence = pathChakras.every(
        (chakra, index) => chakra === currentPhaseChakras[index]
      );
      const isReverseSequence = pathChakras.every(
        (chakra, index) =>
          chakra === currentPhaseChakras[currentPhaseChakras.length - 1 - index]
      );

      // Get the last colored crystal in the path
      const lastColoredCrystal = coloredCrystals[coloredCrystals.length - 1];
      const lastChakra = this.getChakraNameFromColor(lastColoredCrystal.color);

      // Check if this would be the next valid chakra in either sequence
      const lastForwardIndex = currentPhaseChakras.indexOf(lastChakra);
      const lastReverseIndex =
        currentPhaseChakras.length -
        1 -
        currentPhaseChakras.indexOf(lastChakra);
      const nextForwardIndex = currentPhaseChakras.indexOf(endChakra);
      const nextReverseIndex =
        currentPhaseChakras.length - 1 - currentPhaseChakras.indexOf(endChakra);

      // For the final connection, check if it completes a valid sequence
      if (pathChakras.length === currentPhaseChakras.length - 1) {
        // Add this chakra to the path and check if it completes a valid sequence
        const fullPathChakras = [...pathChakras, endChakra];

        // Check if the full path matches either sequence
        const completesForwardSequence = fullPathChakras.every(
          (chakra, index) => chakra === currentPhaseChakras[index]
        );
        const completesReverseSequence = fullPathChakras.every(
          (chakra, index) =>
            chakra ===
            currentPhaseChakras[currentPhaseChakras.length - 1 - index]
        );

        console.log("Final connection validation:", {
          pathChakras,
          endChakra,
          fullPathChakras,
          completesForwardSequence,
          completesReverseSequence,
          currentPhaseChakras,
        });

        return completesForwardSequence || completesReverseSequence;
      }

      // For intermediate connections, check if it continues the sequence
      const isNextInForwardSequence = nextForwardIndex === lastForwardIndex + 1;
      const isNextInReverseSequence = nextReverseIndex === lastReverseIndex - 1;

      console.log("Intermediate connection validation:", {
        pathChakras,
        lastChakra,
        endChakra,
        isForwardSequence,
        isReverseSequence,
        isNextInForwardSequence,
        isNextInReverseSequence,
        currentPhaseChakras,
      });

      return (
        (isForwardSequence && isNextInForwardSequence) ||
        (isReverseSequence && isNextInReverseSequence)
      );
    }

    return false;
  }

  getChakraNameFromColor(color) {
    // Handle null or undefined color
    if (color == null) {
      return null;
    }

    // Convert hex colors to chakra names
    const colorMap = {
      0xe31e24: "red", // Muladhara (Root Chakra)
      0xffa500: "orange", // Svadhishthana (Sacral Chakra)
      0xffed00: "yellow", // Manipura (Solar Plexus Chakra)
      0x00ab4e: "green", // Anahata (Heart Chakra)
      0x00b9e4: "blue", // Vishuddha (Throat Chakra)
      0x0033cc: "indigo", // Ajna (Third Eye Chakra)
      0x9b26b6: "purple", // Sahasrara (Crown Chakra)

      // Add decimal versions of the colors
      14884388: "red", // 0xe31e24
      16753920: "orange", // 0xffa500
      16772352: "yellow", // 0xffed00
      43854: "green", // 0x00ab4e
      47588: "blue", // 0x00b9e4
      13260: "indigo", // 0x0033cc
      10197686: "purple", // 0x9b26b6
    };

    // Add debug logging
    console.log("Color conversion:", {
      inputColor: color,
      inputHex: color != null ? "0x" + color.toString(16) : null,
      chakraName: colorMap[color],
      allMappings: Object.keys(colorMap).map((k) => ({
        key: k,
        value: colorMap[k],
      })),
    });

    return colorMap[color];
  }

  checkValidMovesExist() {
    // Get all colored crystals
    const coloredCrystals = [];
    for (let row = 0; row < this.grid.gridSize; row++) {
      for (let col = 0; col < this.grid.gridSize; col++) {
        const crystal = this.grid.crystals[row][col];
        if (
          crystal &&
          (crystal.type === "positive" || crystal.type === "color")
        ) {
          coloredCrystals.push(crystal);
        }
      }
    }

    // Check if there are any matching pairs
    for (let i = 0; i < coloredCrystals.length; i++) {
      for (let j = i + 1; j < coloredCrystals.length; j++) {
        if (coloredCrystals[i].color === coloredCrystals[j].color) {
          return true; // Found a matching pair
        }
      }
    }

    return false; // No matching pairs found
  }

  checkLevelCompletion() {
    // Check if out of moves
    if (this.moves >= this.maxMoves) {
      if (this.uiManager) {
        this.uiManager.showMessage("Out of Moves!", "gameOver", {
          score: this.score,
          totalScore: this.totalScore + this.score,
          level: this.currentLevel,
          phase: this.currentPhase,
          moves: this.moves,
          maxMoves: this.maxMoves,
          negativeCrystalsRemoved: this.negativeCrystalsRemoved,
          energy: this.energy,
          maxEnergy: this.maxEnergy,
          connectionsMade: this.connectionsMade,
          requiredConnections: this.goals.connectionsRequired,
        });
      }
      return { completed: false, reason: "out_of_moves" };
    }

    // Check if no valid moves left
    if (!this.checkValidMovesExist()) {
      if (this.uiManager) {
        this.uiManager.showMessage("No Valid Moves Left!", "gameOver", {
          score: this.score,
          totalScore: this.totalScore + this.score,
          level: this.currentLevel,
          phase: this.currentPhase,
          moves: this.moves,
          maxMoves: this.maxMoves,
          negativeCrystalsRemoved: this.negativeCrystalsRemoved,
          energy: this.energy,
          maxEnergy: this.maxEnergy,
          connectionsMade: this.connectionsMade,
          requiredConnections: this.goals.connectionsRequired,
        });
      }
      return { completed: false, reason: "no_valid_moves" };
    }

    const goalsCompleted = {
      energy: this.energy >= this.goals.energyToCollect,
      negativeCrystals:
        this.negativeCrystalsRemoved >= this.goals.negativeCrystalsToRemove,
      connections: this.connectionsMade >= this.goals.connectionsRequired,
    };

    if (
      goalsCompleted.energy &&
      goalsCompleted.negativeCrystals &&
      goalsCompleted.connections
    ) {
      if (this.uiManager) {
        // Call onLevelComplete to play the sound
        this.uiManager.onLevelComplete();

        // Show level complete dialog with stats
        this.uiManager.showMessage("Level Complete!", "levelComplete", {
          levelScore: this.score,
          totalScore: this.totalScore + this.score,
          level: this.currentLevel,
          phase: this.currentPhase,
          moves: this.moves,
          maxMoves: this.maxMoves,
          negativeCrystalsRemoved: this.negativeCrystalsRemoved,
          energy: this.energy,
          maxEnergy: this.maxEnergy,
          connectionsMade: this.connectionsMade,
          requiredConnections: this.goals.connectionsRequired,
        });
      }
      return { completed: true, score: this.score };
    }

    return { completed: false };
  }

  nextLevel() {
    // Add current level's score to total
    this.totalScore += this.score;

    const currentLevelInPhase = this.getLevelInPhase();
    const maxLevelsInPhase = this.getLevelsInPhase(this.currentPhase);

    console.log("Level transition:", {
      currentLevel: this.currentLevel,
      currentPhase: this.currentPhase,
      currentLevelInPhase,
      maxLevelsInPhase,
    });

    // Calculate what phase this level should be in
    let totalLevels = 0;
    let targetPhase = 1;
    while (totalLevels < this.currentLevel) {
      totalLevels += this.getLevelsInPhase(targetPhase);
      if (totalLevels < this.currentLevel) {
        targetPhase++;
      }
    }

    // Only show phase completion if we're actually completing the phase
    // NOT if we've skipped to the first level of the next phase
    if (
      currentLevelInPhase === maxLevelsInPhase &&
      this.currentPhase === targetPhase
    ) {
      if (this.uiManager) {
        this.uiManager.showMessage(
          `Phase ${this.currentPhase} Complete!`,
          "phaseComplete",
          {
            phase: this.currentPhase,
            totalScore: this.totalScore,
          }
        );
      }
      this.currentPhase++;
    } else if (this.currentPhase < targetPhase) {
      // If we're behind in phases (due to skipping), just update the phase
      this.currentPhase = targetPhase;
    }

    // Always increment level
    this.currentLevel++;

    console.log("After transition:", {
      newLevel: this.currentLevel,
      newPhase: this.currentPhase,
      targetPhase,
    });

    // Start next level
    this.startLevel(this.currentLevel);
  }

  startLevel(levelNumber, initialLayout = null) {
    this.currentLevel = levelNumber;
    const config = this.getLevelConfig(levelNumber);

    // Reset level state
    this.score = 0;
    this.energy = 0;
    this.moves = 0;
    this.negativeCrystalsRemoved = 0;
    this.connectionsMade = 0;

    // Update level configuration
    this.maxMoves = config.maxMoves;
    this.goals = config.goals;
    this.maxEnergy = config.goals.energyToCollect; // Set maxEnergy based on level goal

    // Update grid configuration
    if (this.grid) {
      this.grid.setLevelConfig(config);

      // If we have an initial layout from the tutorial, use it instead of generating a new one
      if (initialLayout) {
        // Skip creating a new grid since we already have the layout from the tutorial
        console.log("Using initial layout from tutorial");
      } else {
        // Create a new grid with the level configuration
        this.grid.createGrid();
      }
    }

    // Update UI
    this.updateUI();
  }

  getLevelConfig(levelNumber) {
    const phase = this.currentPhase;
    const levelInPhase = this.getLevelInPhase();

    // Base configuration that gets modified based on phase and level
    const baseConfig = {
      maxMoves: 30,
      goals: {
        negativeCrystalsToRemove: 3,
        energyToCollect: 30,
        connectionsRequired: 1,
      },
      gridConfig: {
        positiveRate: 0.6,
        negativeRate: 0.3,
        neutralRate: 0.1,
      },
    };

    switch (phase) {
      case 1: // Single Chakra Phase
        return this.getSingleChakraConfig(levelInPhase);
      case 2: // Dual Chakra Phase
        return this.getDualChakraConfig(levelInPhase);
      case 3: // Triple Chakra Phase
        return this.getTripleChakraConfig(levelInPhase);
      case 4: // Quadruple Chakra Phase
        return this.getQuadrupleChakraConfig(levelInPhase);
      case 5: // Quintuple Chakra Phase
        return this.getQuintupleChakraConfig(levelInPhase);
      case 6: // Sextuple Chakra Phase
        return this.getSextupleChakraConfig(levelInPhase);
      case 7: // All Chakras Phase
        return this.getAllChakrasConfig(levelInPhase);
      default:
        // Game complete
        if (this.uiManager) {
          this.uiManager.showMessage(
            "Congratulations! You've mastered all chakras!",
            "gameComplete",
            {
              totalScore: this.totalScore + this.score,
              finalPhase: 7,
              finalLevel: this.currentLevel,
            }
          );
        }
        return this.getSingleChakraConfig(1); // Return first level config as fallback
    }
  }

  getSingleChakraConfig(level) {
    // Define chakra order consistently with the rest of the game
    const chakraOrder = this.chakraOrder;
    const currentChakra = chakraOrder[level - 1];

    console.log("Single Chakra Config:", {
      level,
      currentChakra,
      chakraOrder,
    });

    const config = {
      maxMoves: 30 - level * 2,
      goals: {
        negativeCrystalsToRemove: 2 + level,
        energyToCollect: 20 + level * 10,
        connectionsRequired: level,
      },
      gridConfig: {
        positiveRate: 0.7,
        negativeRate: 0.2,
        neutralRate: 0.1,
        activeChakras: [currentChakra],
      },
    };

    console.log("Generated config:", config);
    return config;
  }

  getDualChakraConfig(level) {
    // Use chakraOrder to ensure consistency
    const chakraPairs = [
      [this.chakraOrder[0], this.chakraOrder[1]], // red-orange
      [this.chakraOrder[1], this.chakraOrder[2]], // orange-yellow
      [this.chakraOrder[2], this.chakraOrder[3]], // yellow-green
      [this.chakraOrder[3], this.chakraOrder[4]], // green-blue
      [this.chakraOrder[4], this.chakraOrder[5]], // blue-indigo
      [this.chakraOrder[5], this.chakraOrder[6]], // indigo-purple
    ];

    // Ensure level is within bounds
    const adjustedLevel = ((level - 1) % chakraPairs.length) + 1;
    const currentPair = chakraPairs[adjustedLevel - 1];

    console.log("Dual Chakra Config:", {
      requestedLevel: level,
      adjustedLevel,
      currentPair,
      chakraPairs,
      chakraOrder: this.chakraOrder,
    });

    return {
      maxMoves: 25 - adjustedLevel * 2,
      goals: {
        negativeCrystalsToRemove: 3 + adjustedLevel,
        energyToCollect: 40 + adjustedLevel * 15,
        connectionsRequired: 2 * adjustedLevel,
      },
      gridConfig: {
        positiveRate: 0.6,
        negativeRate: 0.3,
        neutralRate: 0.1,
        activeChakras: currentPair,
      },
    };
  }

  getTripleChakraConfig(level) {
    // Use chakraOrder to ensure consistency
    const chakraTriples = [
      [this.chakraOrder[0], this.chakraOrder[1], this.chakraOrder[2]], // red, orange, yellow
      [this.chakraOrder[1], this.chakraOrder[2], this.chakraOrder[3]], // orange, yellow, green
      [this.chakraOrder[2], this.chakraOrder[3], this.chakraOrder[4]], // yellow, green, blue
      [this.chakraOrder[3], this.chakraOrder[4], this.chakraOrder[5]], // green, blue, indigo
      [this.chakraOrder[4], this.chakraOrder[5], this.chakraOrder[6]], // blue, indigo, purple
    ];
    const currentTriple = chakraTriples[level - 1];

    return {
      maxMoves: 22 - level * 2,
      goals: {
        negativeCrystalsToRemove: 4 + level,
        energyToCollect: 60 + level * 20,
        connectionsRequired: 2 + level * 2,
      },
      gridConfig: {
        positiveRate: 0.55,
        negativeRate: 0.35,
        neutralRate: 0.1,
        activeChakras: currentTriple,
      },
    };
  }

  getQuadrupleChakraConfig(level) {
    // Use chakraOrder to ensure consistency
    const chakraQuads = [
      [
        this.chakraOrder[0],
        this.chakraOrder[1],
        this.chakraOrder[2],
        this.chakraOrder[3],
      ], // red to green
      [
        this.chakraOrder[1],
        this.chakraOrder[2],
        this.chakraOrder[3],
        this.chakraOrder[4],
      ], // orange to blue
      [
        this.chakraOrder[2],
        this.chakraOrder[3],
        this.chakraOrder[4],
        this.chakraOrder[5],
      ], // yellow to indigo
      [
        this.chakraOrder[3],
        this.chakraOrder[4],
        this.chakraOrder[5],
        this.chakraOrder[6],
      ], // green to purple
    ];
    const currentQuad = chakraQuads[level - 1];

    return {
      maxMoves: 20 - level * 2,
      goals: {
        negativeCrystalsToRemove: 5 + level,
        energyToCollect: 80 + level * 25,
        connectionsRequired: 3 + level * 2,
      },
      gridConfig: {
        positiveRate: 0.5,
        negativeRate: 0.4,
        neutralRate: 0.1,
        activeChakras: currentQuad,
      },
    };
  }

  getQuintupleChakraConfig(level) {
    // Use chakraOrder to ensure consistency
    const chakraQuints = [
      [
        this.chakraOrder[0],
        this.chakraOrder[1],
        this.chakraOrder[2],
        this.chakraOrder[3],
        this.chakraOrder[4],
      ], // red to blue
      [
        this.chakraOrder[1],
        this.chakraOrder[2],
        this.chakraOrder[3],
        this.chakraOrder[4],
        this.chakraOrder[5],
      ], // orange to indigo
      [
        this.chakraOrder[2],
        this.chakraOrder[3],
        this.chakraOrder[4],
        this.chakraOrder[5],
        this.chakraOrder[6],
      ], // yellow to purple
    ];
    const currentQuint = chakraQuints[level - 1];

    return {
      maxMoves: 18 - level * 2,
      goals: {
        negativeCrystalsToRemove: 6 + level,
        energyToCollect: 100 + level * 30,
        connectionsRequired: 4 + level * 2,
      },
      gridConfig: {
        positiveRate: 0.45,
        negativeRate: 0.45,
        neutralRate: 0.05,
        activeChakras: currentQuint,
      },
    };
  }

  getSextupleChakraConfig(level) {
    // Use chakraOrder to ensure consistency
    const chakraSexts = [
      [
        this.chakraOrder[0],
        this.chakraOrder[1],
        this.chakraOrder[2],
        this.chakraOrder[3],
        this.chakraOrder[4],
        this.chakraOrder[5],
      ], // red to indigo
      [
        this.chakraOrder[1],
        this.chakraOrder[2],
        this.chakraOrder[3],
        this.chakraOrder[4],
        this.chakraOrder[5],
        this.chakraOrder[6],
      ], // orange to purple
    ];
    const currentSext = chakraSexts[level - 1];

    return {
      maxMoves: 18 - level * 2,
      goals: {
        negativeCrystalsToRemove: 7 + level,
        energyToCollect: 120 + level * 40,
        connectionsRequired: 5 + level * 2,
      },
      gridConfig: {
        positiveRate: 0.4,
        negativeRate: 0.5,
        neutralRate: 0.1,
        activeChakras: currentSext,
      },
    };
  }

  getAllChakrasConfig(level) {
    // Use chakraOrder to ensure consistency
    return {
      maxMoves: 20,
      goals: {
        negativeCrystalsToRemove: 10,
        energyToCollect: 150,
        connectionsRequired: 7, // Must connect all chakras in sequence
      },
      gridConfig: {
        positiveRate: 0.5,
        negativeRate: 0.4,
        neutralRate: 0.1,
        activeChakras: this.chakraOrder,
      },
    };
  }

  getLevelInPhase() {
    let levelInPhase = 1;
    let totalLevels = 0;

    // Calculate total levels from previous phases
    for (let phase = 1; phase < this.currentPhase; phase++) {
      totalLevels += this.getLevelsInPhase(phase);
    }

    // Calculate current level within this phase
    levelInPhase = this.currentLevel - totalLevels;

    console.log("getLevelInPhase:", {
      currentLevel: this.currentLevel,
      currentPhase: this.currentPhase,
      totalLevelsFromPreviousPhases: totalLevels,
      levelInPhase,
    });

    return levelInPhase;
  }

  getLevelsInPhase(phase) {
    const levelsPerPhase = {
      1: 7, // Single chakra phase (red through purple)
      2: 6, // Dual chakra phase (red-orange through indigo-purple)
      3: 5, // Triple chakra phase (red-orange-yellow through blue-indigo-purple)
      4: 4, // Quadruple chakra phase (red to green through green to purple)
      5: 3, // Quintuple chakra phase (red to blue through yellow to purple)
      6: 2, // Sextuple chakra phase (red to indigo, orange to purple)
      7: 1, // All chakras phase
    };
    return levelsPerPhase[phase] || 0;
  }

  getGameState() {
    return {
      score: this.score,
      totalScore: this.totalScore,
      level: this.currentLevel,
      phase: this.currentPhase,
      energy: this.energy,
      maxEnergy: this.maxEnergy,
      moves: this.moves,
      maxMoves: this.maxMoves,
      goals: this.goals,
      negativeCrystalsRemoved: this.negativeCrystalsRemoved,
      connectionsMade: this.connectionsMade,
      activeChakras: this.grid ? this.grid.activeChakras : ["red"],
    };
  }

  setupCamera() {
    // Create camera
    const aspect = window.innerWidth / window.innerHeight;
    this.camera = new THREE.PerspectiveCamera(60, aspect, 0.1, 1000);

    // Adjust camera position based on screen size
    const isMobile = window.innerWidth <= 768;
    if (isMobile) {
      // Move camera much closer and lower for mobile
      this.camera.position.set(0, 6, 3); // Dramatically closer position for mobile
      this.camera.fov = 40; // Much narrower FOV for mobile to zoom in more
    } else {
      // Desktop position
      this.camera.position.set(0, 15, 10);
      this.camera.fov = 60;
    }

    this.camera.lookAt(0, 0, 0);
    this.camera.updateProjectionMatrix();

    // Handle window resize
    window.addEventListener("resize", () => {
      const newAspect = window.innerWidth / window.innerHeight;
      this.camera.aspect = newAspect;

      // Update camera position based on new screen size
      const isMobileNow = window.innerWidth <= 768;
      if (isMobileNow) {
        this.camera.position.set(0, 6, 3);
        this.camera.fov = 40;
      } else {
        this.camera.position.set(0, 15, 10);
        this.camera.fov = 60;
      }

      this.camera.lookAt(0, 0, 0);
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(window.innerWidth, window.innerHeight);
    });
  }
}
